/**
 * Ticket services behaviour.
 * As far partners behaviour cannot be described universally server-side,
 * this script defines the behaviour for each partner declaratively.
 * In that, if you need to change the behaviour of a specific partner,
 * change it here.
 *
 * @module kudago/partners
 */

const dialog = require('dialog-component');
const on = require('emmy/on');
const delegate = require('emmy/delegate');
const off = require('emmy/off');
const q = require('queried');
const extend = require('xtend/mutable');
const css = require('mucss/css');
const scrollbarWidth = require('mucss/scrollbar');
const { KudaTip } = require('./utils');

/**
 * Ticket service basic class
 * @constructor
 */
function TicketManager() {
  const self = this;

  self.partners = [];
  self.iframeSrc = null;

  // TODO: call these inits outside the constructor
  // TODO: make the partners static properties

  self.initPartner({
    name: 'eventmag',
  });

  self.initPartner({
    name: 'qtickets',
  });

  self.initPartner({
    name: 'afishakvesty',
    show() {
      return this.showIframe({
        width: 1024,
        height: 680,
      });
    },
  });

  self.initPartner({
    name: 'ponominalu',
    show() {
      return this.showIframe({
        width: 850,
      });
    },
  });

  self.initPartner({
    name: 'radario',
    show() {
      return this.showIframe({
        width: 840,
        height: null,
        processIFrameEvents(event, iframe) {
          if (event.data.radarioAPI) {
            iframe.setAttribute('height', event.data.height);
          }
        },
      });
    },
  });

  self.initPartner({
    name: 'radario_popup',
    show() {
      return this.showIframe({
        width: 840,
        height: null,
        src: self.iframeSrc,
        processIFrameEvents(event, iframe) {
          if (event.data.radarioAPI) {
            iframe.setAttribute('height', event.data.height);
          }
        },
      });
    },
  });

  self.initPartner({
    name: 'timepad',
    show() {
      return this.showIframe({
        width: 600,
        height: null,
        processIFrameEvents(event, iframe) {
          if (event.data.timepad) {
            iframe.setAttribute('height', event.data.height);
          }
        },
      });
    },
  });

  self.initPartner({
    name: 'kassir',
    show() {
      return this.showIframe({
        width: 900,
      });
    },
  });

  self.initPartner({
    name: 'topkvestov_iframe',
    show() {
      return this.showIframe({
        height: 680, // this is width hardcoded in iframe
      });
    },
  });

  self.initPartner({
    name: 'ticketscloud',
    show() {
      return this.showIframe({
        width: 800,
        height: null,
        closable: false,
        processIFrameEvents: (event, iframe) => {
          if (event.origin.indexOf('ticketscloud') > -1) {
            const data = JSON.parse(event.data);
            switch (data.action) {
              case 'setHeight':
                iframe.setAttribute('height', data.height + 60);
                break;
              case 'getDimensions':
              {
                const iframeHeight = iframe.offsetHeight;
                let offset = 0;

                if (iframeHeight > window.innerHeight) {
                  offset = window.innerHeight / 2 - 100;
                }

                iframe.contentWindow.postMessage(JSON.stringify({ action: 'tcDialogOffset', offset }), '*');
                break;
              }
              case 'closeBooking':
                this.dialog.hide();
                break;
            }
          }
        },
      });
    },
  });

  // we don't use our iframe in ticketscloud popup widget (because it has it's
  // own script and widget). However when we click buy button on a mix page
  // we need to go to the item page and open popup automatically
  // that's why we init ticketscloud_popup to use showCurrentPartner method
  // which detects partner name in hash. And rewrite show() manually init
  // ticketscloud widget
  // Moreover there are 2 versions of ticketscloud api on .com and .org domains
  // To provide correct button work we have to choose the api version via our
  // partners admin configuration
  self.initPartner({
    name: 'ticketscloud_popup_com',
    show() {
      on(document, 'DOMContentLoaded', () => {
        q('.ticketscloud_popup_com-ticket-button').click();
      });
    },
  });

  self.initPartner({
    name: 'ticketscloud_popup_org',
    show() {
      on(document, 'DOMContentLoaded', () => {
        q('.ticketscloud_popup_org-ticket-button').click();
      });
    },
  });

  self.showCurrentPartner();
  on(window, 'hashchange', (e) => {
    // eslint-disable-next-line no-restricted-globals
    const hash = location.hash.slice(1);

    if (hash == 'buy-ticket' || hash == 'order') return true;

    e.preventDefault();
    self.showCurrentPartner();
  });

  this.bindIframeSrcToButton();
}

/**
 * basic ticket manager methods
 */
extend(TicketManager.prototype, {
  /**
   * Check does partner present on the page and it needed init it
   * @param  {Object} options Partners ob ject
   * @return {void}
   */
  initPartner(options) {
    const partnerName = options.name;
    let partnersDropdown = null;
    if (q('#ticket-services-dropdown-button-small')) {
      partnersDropdown = q('#ticket-services-dropdown-button-small');
    } else if (q('#ticket-services-dropdown-button-big')) {
      partnersDropdown = q('#ticket-services-dropdown-button-big');
    }
    const button = q(`[data-partner=${partnerName}]`);
    if (button) {
      this.addPartner(options);
    } else if (partnersDropdown) {
      const partnersNames = partnersDropdown.getAttribute('data-partners-names');
      if (partnersNames.includes(partnerName)) {
        this.addPartner(options);
      }
    }
  },

  /**
   * Add partner to the manager partners list
   * @param {Object} partner - Partner options
   * @return {void}
   */
  addPartner(options) {
    this.partners.push(new Partner(options));
  },

  /**
   * Get currently being watch partner (using location hash)
   * @return {Object} current partner
   */
  getCurrentPartner() {
    // eslint-disable-next-line no-restricted-globals
    const hash = location.hash.slice(1);
    const match = /([\w\d]+)-content/.exec(hash);
    const partnerName = match ? match[1] : null;
    let output = null;

    if (partnerName) {
      this.partners.forEach((partner) => {
        if (partner.name == partnerName) {
          output = partner;
        }
      });
    }

    return output;
  },
  /**
   * Show interface of partner which is curretnly actual
   * @return {void}
   */
  showCurrentPartner() {
    const partner = this.getCurrentPartner();
    if (partner) partner.show();
  },

  bindIframeSrcToButton() {
    delegate(document, 'click', '[data-partner][data-href]', (e) => {
      const target = e.delegateTarget;
      this.iframeSrc = target.getAttribute('data-href');
    });
  },
});

/**
 * Generic partner
 * @param {Object} options initialization options
 * @constructor
 */
function Partner(options) {
  extend(this, options);
}

/**
 * Show interface of the partner
 * @return {void}
 */
Partner.prototype.show = function () {
  this.showIframe();
};

/**
 * General kind of function to show partners iFrame
 * @param  {Object} options iFrame call process options
 * @return {DOM object} iFrame object
 */
Partner.prototype.showIframe = function (options) {
  const self = this;

  // default settings
  const defaults = {
    width: 800,
    height: 600,
    closable: true,
    processIFrameEvents() {},
    getSrc() {
      throw new Error('getSrc is a required setting');
    },
  };
  options = extend(defaults, options);

  // create iframe
  const iframe = document.createElement('iframe');
  iframe.id = `${self.name}-iframe`;
  iframe.classList.add('ticket-popup');
  iframe.scrollable = 'no';

  // get partner data
  const button = q(`[data-partner=${self.name}]`);
  iframe.src = options.src || button.getAttribute('data-href');

  css(iframe, {
    width: options.width,
    height: options.height,
  });

  // remove body scrollbars
  css(document.body, {
    overflow: 'hidden',
    'margin-right': scrollbarWidth,
  });

  // hide tooltip (if popup is opened from it)
  KudaTip.hideAll();

  // bind window on message so iframe can adjust its height
  function applyHeight(event) {
    options.processIFrameEvents(event, iframe);
  }
  on(window, 'message', applyHeight);

  function closeDialog() {
    // eslint-disable-next-line no-restricted-globals
    history.replaceState(null, null, '#');
    css(document.body, {
      overflow: null,
      'margin-right': null,
    });
    // remove window listener
    off(window, 'message', applyHeight);
  }

  // show iframe in dialog
  self.dialog = dialog(null, iframe)
    .overlay()
    .escapable();

  if (options.closable) self.dialog.closable();

  self.dialog.addClass('dialog-partner')
    .addClass('dialog-big')
    .on('close', closeDialog)
    .on('hide', closeDialog)
    .show();

  // fullscreen dialog with scrolls
  on(self.dialog.el, 'click', (e) => {
    if (self.dialog.el !== e.target) return;
    closeDialog();
    self.dialog.hide();
  });

  // add hash to the history
  // eslint-disable-next-line no-restricted-globals
  history.replaceState(null, null, `#${self.name}-content`);

  return iframe;
};

module.exports = TicketManager;
